import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { formatPhoneNumber } from "@/lib/formatPhone";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Check, X, Loader2, Trash2, Search, ArrowUpDown,
  Download, AlertTriangle, Upload, CheckCheck,
} from "lucide-react";
import ExcelJS from "exceljs";

type CertificateRequest = {
  id: string; first_name: string; last_name: string; email: string;
  phone: string; group_number: string; status: string; created_at: string;
  is_duplicate?: boolean;
};

type SortField = "created_at" | "first_name" | "status" | "group_number";
type SortDir = "asc" | "desc";

type Props = {
  sessionToken: string;
  requests: CertificateRequest[];
  loading: boolean;
  fetchRequests: () => void;
  handleAction: (id: string, action: "approve" | "reject" | "delete") => void;
  processingId: string | null;
  courses: { id: string; title: string; certificate_template_path: string | null }[];
  fetchCourses: () => void;
  highlightRequestId?: string | null;
  defaultCertTab?: string;
};

const formatName = (name: string) => name.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ');

const CertificatesTab = ({ sessionToken, requests, loading, fetchRequests, handleAction, processingId, courses, fetchCourses, highlightRequestId, defaultCertTab }: Props) => {
  const { toast } = useToast();
  const [searchQuery, setSearchQuery] = useState("");
  const [sortField, setSortField] = useState<SortField>("created_at");
  const [sortDir, setSortDir] = useState<SortDir>("desc");
  const certFileRef = useRef<HTMLInputElement>(null);
  const [uploadingTemplateFor, setUploadingTemplateFor] = useState<string | null>(null);
  const [approvingAll, setApprovingAll] = useState(false);
  const highlightRef = useRef<HTMLTableRowElement>(null);
  const [searchFocused, setSearchFocused] = useState(false);

  useEffect(() => {
    if (highlightRequestId && highlightRef.current) {
      highlightRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [highlightRequestId, requests]);

  const toggleSort = (field: SortField) => {
    if (sortField === field) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortField(field); setSortDir("asc"); }
  };

  const SortButton = ({ field, children }: { field: SortField; children: React.ReactNode }) => (
    <button className="flex items-center gap-1 hover:text-foreground" onClick={() => toggleSort(field)}>
      {children}
      <ArrowUpDown className={`w-3 h-3 ${sortField === field ? "text-primary" : "text-muted-foreground/50"}`} />
    </button>
  );

  const getFilteredRequests = (courseFilter?: string) => {
    let filtered = requests;
    if (courseFilter) {
      filtered = filtered.filter((r) => r.group_number === `course-${courseFilter}`);
    } else {
      filtered = filtered.filter((r) => !r.group_number.startsWith("course-"));
    }
    return filtered
      .filter((r) => {
        if (!searchQuery) return true;
        const q = searchQuery.toLowerCase();
        return r.first_name.toLowerCase().includes(q) || r.last_name.toLowerCase().includes(q) ||
          r.email.toLowerCase().includes(q) || r.phone.includes(q) || r.status.toLowerCase().includes(q) ||
          r.group_number.toLowerCase().includes(q) || `group ${r.group_number}`.toLowerCase().includes(q);
      })
      .sort((a, b) => {
        const dir = sortDir === "asc" ? 1 : -1;
        if (sortField === "created_at") return dir * (new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
        return dir * (a[sortField] ?? "").localeCompare(b[sortField] ?? "");
      });
  };

  const handleApproveAll = async (courseFilter?: string) => {
    const filtered = getFilteredRequests(courseFilter);
    const pending = filtered.filter((r) => r.status === "pending");
    if (pending.length === 0) {
      toast({ title: "No pending requests to approve" });
      return;
    }
    setApprovingAll(true);
    try {
      let successCount = 0;
      for (const req of pending) {
        try {
          const res = await supabase.functions.invoke("manage-certificates", {
            body: { action: "approve", sessionToken, requestId: req.id },
          });
          if (res.data?.success) successCount++;
        } catch {}
      }
      toast({ title: `Approved ${successCount}/${pending.length} certificates` });
      fetchRequests();
    } catch {
      toast({ title: "Error approving all", variant: "destructive" });
    } finally { setApprovingAll(false); }
  };

  const handleExport = async (courseFilter?: string) => {
    const filtered = getFilteredRequests(courseFilter);
    const workbook = new ExcelJS.Workbook();
    const sheet = workbook.addWorksheet("Certificate Requests");
    sheet.columns = [
      { header: "First Name", key: "first_name", width: 15 },
      { header: "Last Name", key: "last_name", width: 15 },
      { header: "Email", key: "email", width: 25 },
      { header: "Phone", key: "phone", width: 15 },
      { header: "Status", key: "status", width: 10 },
      { header: "Date", key: "date", width: 12 },
    ];
    filtered.forEach((r) => {
      sheet.addRow({
        first_name: r.first_name, last_name: r.last_name, email: r.email,
        phone: formatPhoneNumber(r.phone), status: r.status, date: new Date(r.created_at).toLocaleDateString(),
      });
    });
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url;
    a.download = `certificate_requests_${new Date().toISOString().slice(0, 10)}.xlsx`;
    a.click(); URL.revokeObjectURL(url);
    toast({ title: "Exported!", description: `${filtered.length} records exported.` });
  };

  const handleUploadTemplate = async (courseId: string, file: File) => {
    setUploadingTemplateFor(courseId);
    try {
      const res = await supabase.functions.invoke("manage-certificates", {
        body: { action: "get_cert_upload_url", sessionToken, courseId, fileName: file.name },
      });
      if (res.error || !res.data?.success) throw new Error(res.data?.error || "Failed");
      const { error: uploadError } = await supabase.storage
        .from("certificates")
        .uploadToSignedUrl(res.data.path, res.data.token, file);
      if (uploadError) throw uploadError;
      toast({ title: "Certificate template uploaded!" });
      fetchCourses();
    } catch (err) {
      const error = err as Error;
      toast({ title: "Upload failed", description: err.message, variant: "destructive" });
    } finally { setUploadingTemplateFor(null); }
  };

  // Compute unique statuses and groups for filter suggestions
  const allStatuses = [...new Set(requests.filter(r => !r.group_number.startsWith("course-")).map(r => r.status))];
  const allGroups = [...new Set(requests.filter(r => !r.group_number.startsWith("course-")).map(r => r.group_number))];
  const showDropdown = searchFocused && !searchQuery;

  const renderRequestsTable = (filtered: CertificateRequest[], courseFilter?: string) => (
    <Card>
      <CardContent className="p-0 overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead><SortButton field="first_name">Name</SortButton></TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Phone</TableHead>
              {!courseFilter && <TableHead><SortButton field="group_number">Group</SortButton></TableHead>}
              <TableHead><SortButton field="status">Status</SortButton></TableHead>
              <TableHead><SortButton field="created_at">Date</SortButton></TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={courseFilter ? 6 : 7} className="text-center py-8 text-muted-foreground">
                  {loading ? "Loading..." : "No requests found"}
                </TableCell>
              </TableRow>
            ) : (
              filtered.map((req) => (
                <TableRow
                  key={req.id}
                  ref={highlightRequestId === req.id ? highlightRef : undefined}
                  className={highlightRequestId === req.id ? "bg-primary/10 ring-1 ring-primary/30 transition-colors duration-1000" : ""}
                >
                  <TableCell className="font-medium">
                    <div className="flex items-center gap-2">
                      {formatName(req.first_name)} {formatName(req.last_name)}
                      {req.is_duplicate && <span title="Duplicate"><AlertTriangle className="w-4 h-4 text-amber-500" /></span>}
                    </div>
                  </TableCell>
                  <TableCell>{req.email}</TableCell>
                  <TableCell className="font-mono whitespace-nowrap text-sm">{formatPhoneNumber(req.phone)}</TableCell>
                  {!courseFilter && (
                    <TableCell>
                      <Badge variant="outline" className="text-xs">{req.group_number.startsWith("course-") ? "Course" : (req.group_number.toLowerCase().startsWith("group") ? req.group_number : `Group ${req.group_number}`)}</Badge>
                    </TableCell>
                  )}
                  <TableCell>
                    <Badge variant={req.status === "pending" ? "secondary" : req.status === "approved" ? "default" : "destructive"}>
                      {req.status}
                    </Badge>
                  </TableCell>
                  <TableCell>{new Date(req.created_at).toLocaleDateString()}</TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      {(req.status === "pending" || req.status === "rejected") && (
                        <>
                          <Button size="sm" variant="default" onClick={() => handleAction(req.id, "approve")} disabled={processingId === req.id} title="Approve">
                            {processingId === req.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                          </Button>
                          {req.status === "pending" && (
                            <Button size="sm" variant="destructive" onClick={() => handleAction(req.id, "reject")} disabled={processingId === req.id} title="Reject">
                              <X className="w-4 h-4" />
                            </Button>
                          )}
                        </>
                      )}
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button size="sm" variant="outline" disabled={processingId === req.id}><Trash2 className="w-4 h-4" /></Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete request?</AlertDialogTitle>
                            <AlertDialogDescription>Remove {req.first_name} {req.last_name}'s request permanently.</AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction onClick={() => handleAction(req.id, "delete")}>Delete</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );

  const renderCourseTab = (courseId: string) => {
    const course = courses.find((c) => c.id === courseId);
    const filtered = getFilteredRequests(courseId);
    const pendingCount = filtered.filter((r) => r.status === "pending").length;

    return (
      <div className="space-y-4">
        <Card>
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium">Certificate Template</p>
                <p className="text-xs text-muted-foreground">
                  {course?.certificate_template_path ? "Custom template uploaded" : "Using default template"}
                </p>
              </div>
              <div className="flex gap-2">
                <input ref={certFileRef} type="file" accept=".pdf" className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file && courseId) handleUploadTemplate(courseId, file);
                    if (e.target) e.target.value = "";
                  }}
                />
                <Button size="sm" variant="outline"
                  onClick={() => certFileRef.current?.click()}
                  disabled={uploadingTemplateFor === courseId}>
                  {uploadingTemplateFor === courseId ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Upload className="w-4 h-4 mr-1" />}
                  Upload Template
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">{filtered.length} request(s){pendingCount > 0 ? ` • ${pendingCount} pending` : ""}</p>
          <div className="flex gap-2">
            {pendingCount > 0 && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button size="sm" disabled={approvingAll}>
                    {approvingAll ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <CheckCheck className="w-4 h-4 mr-1" />}
                    Approve All ({pendingCount})
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Approve all pending certificates?</AlertDialogTitle>
                    <AlertDialogDescription>This will approve and send certificates to all {pendingCount} pending requests.</AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={() => handleApproveAll(courseId)}>Approve All</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
            <Button variant="outline" size="sm" onClick={() => handleExport(courseId)} disabled={filtered.length === 0}>
              <Download className="w-4 h-4 mr-1" /> Export
            </Button>
          </div>
        </div>

        {renderRequestsTable(filtered, courseId)}
      </div>
    );
  };

  const legacyRequests = getFilteredRequests();
  const legacyPendingCount = legacyRequests.filter((r) => r.status === "pending").length;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold">Certificate Requests</h2>
        <Button variant="outline" onClick={fetchRequests} disabled={loading}>
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Refresh"}
        </Button>
      </div>

      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          placeholder="Search by name, email, status, group..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onFocus={() => setSearchFocused(true)}
          onBlur={() => setTimeout(() => setSearchFocused(false), 150)}
          className="pl-9"
        />
        {/* Dropdown suggestions on focus */}
        {showDropdown && (allStatuses.length > 0 || allGroups.length > 0) && (
          <div className="absolute top-full left-0 right-0 z-50 mt-1 rounded-md border border-border bg-popover shadow-md p-2 space-y-2">
            {allStatuses.length > 0 && (
              <div>
                <p className="text-xs text-muted-foreground mb-1 px-1">Status</p>
                <div className="flex flex-wrap gap-1">
                  {allStatuses.map((s) => (
                    <button key={s} onMouseDown={() => setSearchQuery(s)}
                      className="px-2 py-0.5 text-xs rounded-full border bg-muted border-border text-muted-foreground hover:bg-accent">
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {allGroups.length > 0 && (
              <div>
                <p className="text-xs text-muted-foreground mb-1 px-1">Group</p>
                <div className="flex flex-wrap gap-1">
                  {allGroups.sort().map((g) => (
                    <button key={g} onMouseDown={() => setSearchQuery(g)}
                      className="px-2 py-0.5 text-xs rounded-full border bg-muted border-border text-muted-foreground hover:bg-accent">
                      {g}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
        {searchQuery && (
          <button onClick={() => setSearchQuery("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {courses.length === 0 ? (
        <>
          <div className="flex justify-end gap-2">
            {legacyPendingCount > 0 && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button size="sm" disabled={approvingAll}>
                    {approvingAll ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <CheckCheck className="w-4 h-4 mr-1" />}
                    Approve All ({legacyPendingCount})
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Approve all pending certificates?</AlertDialogTitle>
                    <AlertDialogDescription>This will approve and send certificates to all {legacyPendingCount} pending requests.</AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={() => handleApproveAll()}>Approve All</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
            <Button variant="outline" size="sm" onClick={() => handleExport()} disabled={legacyRequests.length === 0}>
              <Download className="w-4 h-4 mr-1" /> Export
            </Button>
          </div>
          {renderRequestsTable(legacyRequests)}
        </>
      ) : (
        <Tabs defaultValue={defaultCertTab || "legacy"} key={defaultCertTab}>
          <TabsList>
            <TabsTrigger value="legacy">Manual Requests</TabsTrigger>
            {courses.map((c) => (
              <TabsTrigger key={c.id} value={c.id}>{c.title}</TabsTrigger>
            ))}
          </TabsList>
          <TabsContent value="legacy">
            <div className="flex justify-end mb-4 gap-2">
              {legacyPendingCount > 0 && (
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button size="sm" disabled={approvingAll}>
                      {approvingAll ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <CheckCheck className="w-4 h-4 mr-1" />}
                      Approve All ({legacyPendingCount})
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Approve all pending certificates?</AlertDialogTitle>
                      <AlertDialogDescription>This will approve all {legacyPendingCount} pending requests.</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction onClick={() => handleApproveAll()}>Approve All</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}
              <Button variant="outline" size="sm" onClick={() => handleExport()} disabled={legacyRequests.length === 0}>
                <Download className="w-4 h-4 mr-1" /> Export
              </Button>
            </div>
            {renderRequestsTable(legacyRequests)}
          </TabsContent>
          {courses.map((c) => (
            <TabsContent key={c.id} value={c.id}>
              {renderCourseTab(c.id)}
            </TabsContent>
          ))}
        </Tabs>
      )}
    </div>
  );
};

export default CertificatesTab;
